import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isPlatform } from '@ionic/react';
import { Textarea_v2 as Textarea, Typography, TypographyVariantsEnum, useIsKeyBoardShown } from '@mindly/ui-components';

type Tag = {
  id: string;
  name: string;
};

type TagsListProps = {
  tags: Tag[];
  selected: Tag[];
  isTrialSession: boolean;
  comment: string;
  onClick: (tag: Tag) => void;
  onCommentChange: (comment: string) => void;
  onCommentBlur: () => void;
};

export const TagsList: React.FC<TagsListProps> = ({
  tags,
  selected,
  isTrialSession,
  comment,
  onCommentChange,
  onCommentBlur,
  onClick,
}) => {
  const isKeyboardShown = useIsKeyBoardShown();
  const textareaRef = useRef<HTMLIonTextareaElement>(null);
  const [fadeoutAnimation, setFadeoutAnimation] = useState(false);
  const previousSelected = useRef<Tag[]>(selected);
  const [isFocused, setIsFocused] = useState(false);
  const { t } = useTranslation('reviews');

  useEffect(() => {
    setFadeoutAnimation(true);
  }, [tags]);

  useEffect(() => {
    const hasOtherTag = selected[selected.length - 1]?.id === 'other';
    const isValueAdded = previousSelected.current.length < selected.length;
    if (hasOtherTag && isValueAdded && textareaRef.current) {
      requestAnimationFrame(() => {
        textareaRef.current?.setFocus();
      });
    }
    previousSelected.current = selected;
  }, [selected]);

  const handleTagClick = (tag: Tag) => {
    onClick(tag);
  };

  useEffect(() => {
    if ((!isPlatform('hybrid') || isKeyboardShown) && isFocused && textareaRef.current) {
      textareaRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isFocused, isKeyboardShown]);

  const handleTextareaFocus = () => {
    setIsFocused(true);
  };

  const handleTextareaBlur = () => {
    setIsFocused(false);
    onCommentBlur();
  };

  return (
    <div className="flex flex-col gap-3 flex-1 justify-between">
      <div
        className={`flex items-center justify-center flex-wrap gap-2 transition-opacity duration-500 opacity-0 ${
          fadeoutAnimation ? 'opacity-100' : ''
        }`}
      >
        {tags.map((tag) => (
          <Typography
            variant={TypographyVariantsEnum.Text14Semi}
            key={tag.id}
            onClick={() => handleTagClick(tag)}
            className={`rounded-[48px] cursor-pointer px-3 py-1.5 ${
              selected.some((_) => _.id === tag.id)
                ? '!bg-color-background-gray-black !text-color-white'
                : '!bg-color-background-anti-flash-light !text-color-gray-middle'
            }`}
          >
            {tag.name}
          </Typography>
        ))}
      </div>
      {selected.some((tag) => tag.id === 'other') && (
        <Textarea
          ref={textareaRef}
          placeholder={t?.(
            isTrialSession ? 'rate.first.session.comment.placeholder' : 'reviews.add.review.placeholder'
          )}
          value={comment}
          onIonChange={(e) => onCommentChange(e.detail.value ?? '')}
          onIonBlur={handleTextareaBlur}
          onIonFocus={handleTextareaFocus}
          showCounter={false}
          className="w-full h-[100px] !mt-4 py-4 border-t border-color-background-gray-middle/50 resize-none text-[16px] leading-[24px] text-color-black focus:outline-none"
          classNameCounter="-bottom-4"
          wrapperClassName="-top-4"
        />
      )}
    </div>
  );
};
